import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  RiMapPin2Fill,
  RiPhoneLine,
  RiTimeLine,
  RiPriceTag3Line,
  RiShieldCheckLine,
  RiErrorWarningLine,
  RiSearchEyeLine,
  RiRefreshLine,
  RiBox3Line,
} from '@remixicon/react';
import { Spinner } from '@/components/ui/spinner';
import {
  ShipmentStatusBadge,
  getShipmentStatusLabel,
} from '@/pages/shipments/ShipmentStatusBadge';
import { TrackShell } from './TrackShell';
import {
  getPublicTrack,
  PublicTrack,
  PublicTrackNotFoundError,
  PublicTrackRateLimitedError,
  PublicTrackParty,
  PublicTrackTimelineEvent,
} from '@/services/api/publicTrack';

// Public, unauthenticated tracking page (route `/track/:waybill`, mounted
// OUTSIDE ProtectedRoute). A government assessor scans a label QR and lands
// here on a stock phone — no login, no app. All PII is already masked by the
// server; this page only presents what it receives, rendering "—" for any
// null field. Mobile-first: assessors scan on phones.

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: PublicTrack }
  | { kind: 'not_found' }
  | { kind: 'rate_limited' }
  | { kind: 'error' };

const DASH = '—';

const formatTariff = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // Unknown currency code — fall back to a plain grouped number.
    return `${currency} ${amount.toLocaleString('id-ID')}`;
  }
};

const val = (v: string | null): string => (v && v.trim() ? v : DASH);

// Small label-over-value cell used in the Informasi Kiriman grid.
const Field: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-text-sub-600">{label}</span>
    <span className="text-sm font-medium text-text-strong-950">{value}</span>
  </div>
);

// One party in the origin → destination route. `dot` shows a hollow ring for
// the origin and a filled pin for the destination, connected by the caller's
// vertical rail.
const RouteParty: React.FC<{
  role: 'origin' | 'destination';
  title: string;
  party: PublicTrackParty;
}> = ({ role, title, party }) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center pt-1">
      {role === 'origin' ? (
        <span className="mt-0.5 h-3 w-3 rounded-full border-2 border-primary bg-bg-white-0" />
      ) : (
        <RiMapPin2Fill className="size-4 text-primary" />
      )}
      {role === 'origin' && (
        <span className="my-1 w-px flex-1 border-l border-dashed border-stroke-sub-300" />
      )}
    </div>
    <div className="flex-1 pb-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-text-soft-400">
        {title}
      </span>
      <p className="mt-0.5 text-sm font-semibold text-text-strong-950">
        {val(party.name)}
      </p>
      <p className="mt-1 text-sm leading-snug text-text-sub-600 break-words">
        {val(party.address)}
      </p>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
        <span className="inline-flex items-center gap-1 text-xs text-text-sub-600">
          <span className="text-text-soft-400">Kode Pos</span>
          <span className="font-medium text-text-strong-950">
            {val(party.postalCode)}
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-text-sub-600">
          <RiPhoneLine className="size-3.5 text-text-soft-400" />
          <span className="font-medium text-text-strong-950">
            {val(party.phone)}
          </span>
        </span>
      </div>
    </div>
  </div>
);

// Horizontal lifecycle stepper (DHL-style). Maps the current status to a step;
// terminal non-happy states (returned/failed/cancelled) recolor the final node.
const STEP_LABELS = ['Dibuat', 'Di Hub', 'Dikirim', 'Diantar', 'Selesai'];
const STATUS_STEP: Record<string, number> = {
  CREATED: 0,
  AT_HUB: 1,
  PARTIALLY_DISPATCHED: 2,
  DISPATCHED: 2,
  IN_DELIVERY: 3,
  IN_DISPUTE: 3,
  COMPLETED: 4,
  RETURNED: 4,
  FAILED: 4,
  CANCELLED: 4,
};
const TERMINAL_ALT: Record<string, { label: string; danger: boolean }> = {
  RETURNED: { label: 'Dikembalikan', danger: false },
  FAILED: { label: 'Gagal', danger: true },
  CANCELLED: { label: 'Dibatalkan', danger: true },
};

const ProgressStepper: React.FC<{
  status: string;
  hubName: string | null;
  courier: string | null;
}> = ({ status, hubName, courier }) => {
  const current = STATUS_STEP[status] ?? 0;
  const alt = TERMINAL_ALT[status];
  // Extra context under specific steps: hub at "Di Hub" (1), courier at "Diantar" (3).
  const subFor = (i: number): string | null =>
    i === 1 ? hubName : i === 3 ? courier : null;
  return (
    <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
      <div className="flex items-start">
        {STEP_LABELS.map((label, i) => {
          const done = i <= current;
          const isFinal = i === STEP_LABELS.length - 1;
          const danger = isFinal && alt?.danger;
          const nodeColor = danger
            ? 'bg-error-base'
            : done
              ? 'bg-primary'
              : 'bg-stroke-sub-300';
          const sub = subFor(i);
          return (
            <React.Fragment key={label}>
              <div className="flex w-0 flex-1 flex-col items-center gap-1.5">
                <span
                  className={`size-2.5 rounded-full ${nodeColor} ${
                    i === current ? 'ring-4 ring-primary/15' : ''
                  }`}
                />
                <span
                  className={`px-0.5 text-center text-[10px] leading-tight ${
                    done
                      ? 'font-medium text-text-strong-950'
                      : 'text-text-soft-400'
                  }`}
                >
                  {isFinal && alt ? alt.label : label}
                </span>
                {sub && (
                  <span className="px-0.5 text-center text-[9px] leading-tight text-text-sub-600">
                    {sub}
                  </span>
                )}
              </div>
              {!isFinal && (
                <span
                  className={`mt-[4px] h-0.5 w-full flex-1 ${
                    i < current ? 'bg-primary' : 'bg-stroke-soft-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// Vertical status history, newest-first. The top (current) entry is emphasized
// with a filled, ringed dot; earlier ones are muted — the standard courier
// tracking pattern.
const StatusTimeline: React.FC<{ events: PublicTrackTimelineEvent[] }> = ({
  events,
}) => (
  <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
    <div className="mb-4 flex items-center gap-2">
      <RiTimeLine className="size-4 text-primary" />
      <h2 className="text-sm font-semibold text-text-strong-950">
        Riwayat Status
      </h2>
    </div>
    <ol className="flex flex-col">
      {events.map((e, i) => {
        const current = i === 0;
        const last = i === events.length - 1;
        return (
          <li key={`${e.status}-${e.occurredAt}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={`mt-1 size-2.5 rounded-full ${
                  current
                    ? 'bg-primary ring-4 ring-primary/15'
                    : 'bg-stroke-sub-300'
                }`}
              />
              {!last && <span className="my-1 w-px flex-1 bg-stroke-soft-200" />}
            </div>
            <div className={last ? '' : 'pb-5'}>
              <p
                className={`text-sm ${
                  current
                    ? 'font-semibold text-text-strong-950'
                    : 'font-medium text-text-sub-600'
                }`}
              >
                {getShipmentStatusLabel(e.status)}
              </p>
              <p className="mt-0.5 text-xs text-text-soft-400">
                {dayjs(e.occurredAt).format('DD MMM YYYY, HH:mm')}
                {e.location ? ` · ${e.location}` : ''}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  </div>
);

const StateCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-6 text-center shadow-card-xs">
    <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-bg-weak-50">
      {icon}
    </div>
    <h1 className="text-base font-semibold text-text-strong-950">{title}</h1>
    {children}
  </div>
);

const PublicTrackPage: React.FC = () => {
  const { waybill } = useParams<{ waybill: string }>();
  const navigate = useNavigate();
  const goBack = () => navigate('/track');
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  const load = useCallback(async () => {
    if (!waybill) {
      setState({ kind: 'not_found' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const data = await getPublicTrack(waybill);
      setState({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof PublicTrackNotFoundError) {
        setState({ kind: 'not_found' });
      } else if (err instanceof PublicTrackRateLimitedError) {
        setState({ kind: 'rate_limited' });
      } else {
        setState({ kind: 'error' });
      }
    }
  }, [waybill]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.kind === 'loading') {
    return (
      <TrackShell onBack={goBack}>
        <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-10 text-center shadow-card-xs">
          <div className="flex flex-col items-center gap-4">
            <Spinner size="xl" tone="primary" />
            <p className="text-sm text-text-sub-600">Memuat data pengiriman…</p>
          </div>
        </div>
      </TrackShell>
    );
  }

  if (state.kind === 'not_found') {
    return (
      <TrackShell onBack={goBack}>
        <StateCard
          icon={<RiSearchEyeLine className="size-6 text-text-sub-600" />}
          title="Pengiriman tidak ditemukan"
        >
          <p className="mt-1.5 text-sm text-text-sub-600">
            Nomor resi{' '}
            <span className="font-medium text-text-strong-950">{waybill}</span>{' '}
            tidak terdaftar. Periksa kembali kode pada label.
          </p>
        </StateCard>
      </TrackShell>
    );
  }

  if (state.kind === 'rate_limited') {
    return (
      <TrackShell onBack={goBack}>
        <StateCard
          icon={<RiTimeLine className="size-6 text-text-sub-600" />}
          title="Terlalu banyak permintaan"
        >
          <p className="mb-4 mt-1.5 text-sm text-text-sub-600">
            Anda melakukan terlalu banyak permintaan. Coba lagi sebentar.
          </p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-static-white transition-opacity hover:opacity-90"
          >
            <RiRefreshLine className="size-4" />
            Coba Lagi
          </button>
        </StateCard>
      </TrackShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <TrackShell onBack={goBack}>
        <StateCard
          icon={<RiErrorWarningLine className="size-6 text-error-base" />}
          title="Gagal memuat data"
        >
          <p className="mb-4 mt-1.5 text-sm text-text-sub-600">
            Terjadi gangguan koneksi. Silakan coba lagi.
          </p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-static-white transition-opacity hover:opacity-90"
          >
            <RiRefreshLine className="size-4" />
            Coba Lagi
          </button>
        </StateCard>
      </TrackShell>
    );
  }

  const { data } = state;

  return (
    <TrackShell onBack={goBack}>
      {/* Status card (overlaps the hero) */}
      <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-md">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="text-xs text-text-sub-600">Nomor Resi</span>
            <p className="break-all text-lg font-bold leading-tight tracking-tight text-text-strong-950">
              {data.waybill}
            </p>
          </div>
          <ShipmentStatusBadge status={data.status} />
        </div>
        <div className="mt-4 flex items-center gap-1.5 border-t border-stroke-soft-200 pt-3 text-xs text-text-sub-600">
          <RiTimeLine className="size-3.5 text-text-soft-400" />
          Diperbarui {dayjs(data.updatedAt).format('DD MMM YYYY, HH:mm')}
        </div>
      </div>

      {/* Lifecycle progress */}
      <ProgressStepper
        status={data.status}
        hubName={data.hubName}
        courier={data.courier}
      />

      {/* Status history */}
      {data.timeline.length > 0 && <StatusTimeline events={data.timeline} />}

      {/* Route: origin → destination */}
      <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
        <div className="flex flex-col gap-1">
          <RouteParty role="origin" title="Pengirim" party={data.sender} />
          <RouteParty
            role="destination"
            title="Penerima"
            party={data.receiver}
          />
        </div>
      </div>

      {/* Informasi Kiriman */}
      <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
        <div className="mb-3 flex items-center gap-2">
          <RiBox3Line className="size-4 text-primary" />
          <h2 className="text-sm font-semibold text-text-strong-950">
            Informasi Kiriman
          </h2>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Jumlah Barang" value={`${data.shipment.itemCount}`} />
          <Field label="Jumlah Koli" value={`${data.shipment.totalKoli}`} />
          <Field label="Berat" value={`${data.shipment.weightKg} kg`} />
        </div>
      </div>

      {/* Tariff */}
      <div className="flex items-center justify-between rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-5 shadow-card-xs">
        <div className="flex items-center gap-3">
          <div
            className="flex size-10 items-center justify-center rounded-full"
            style={{ backgroundColor: 'var(--primary-alpha-10)' }}
          >
            <RiPriceTag3Line className="size-5 text-primary" />
          </div>
          <div>
            <span className="text-xs text-text-sub-600">Tarif Layanan</span>
            <p className="text-sm font-medium text-text-strong-950">
              {data.tariff.serviceName}
            </p>
          </div>
        </div>
        <span className="text-lg font-bold text-text-strong-950">
          {formatTariff(data.tariff.amount, data.tariff.currency)}
        </span>
      </div>

      <p className="mt-1 flex items-center justify-center gap-1.5 px-2 text-center text-[11px] leading-relaxed text-text-soft-400">
        <RiShieldCheckLine className="size-3.5 shrink-0" />
        Sebagian data pribadi disamarkan demi perlindungan data (UU PDP).
      </p>
    </TrackShell>
  );
};

export default PublicTrackPage;
